"use client";

// CRITICAL: @datadog/browser-logs must NEVER be imported statically here.
// Its entry point runs makeLogsPublicApi() -> startBufferingData() ->
// initFetchObservable() at IMPORT time, which wraps globalThis.fetch. When this
// module is evaluated on the server (SSR of the client components that import
// it), that wrapper hides Next.js's patched-fetch marker, Next patches fetch a
// SECOND time, and any cache-eligible POST (e.g. the TradeZero OAuth token
// exchange) deadlocks forever on the incremental-cache lock (root-caused
// 2026-07-30). Load it lazily, in the browser only.
import type { datadogLogs as DatadogLogs } from "@datadog/browser-logs";

const DD_SITE = "us5.datadoghq.com";
const DD_SERVICE = "quantwheel-web";
const SLOW_API_THRESHOLD_MS = 5000;

let dd: typeof DatadogLogs | null = null;
let initialized = false;

export function initDatadog(): void {
    if (initialized || typeof window === "undefined") return;

    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    if (!clientToken) return;

    initialized = true;

    import("@datadog/browser-logs")
        .then(({ datadogLogs }) => {
            dd = datadogLogs;
            datadogLogs.init({
                clientToken,
                site: DD_SITE,
                service: DD_SERVICE,
                env: process.env.NODE_ENV,
                forwardErrorsToLogs: true,
                sessionSampleRate: 100,
                beforeSend: (log) => {
                    if (
                        typeof log.message === "string" &&
                        log.message.includes("ResizeObserver")
                    ) {
                        return false;
                    }
                    return true;
                },
            });
        })
        .catch(() => {
            initialized = false;
        });
}

export function setDatadogUser(user: {
    id?: string | null;
    email?: string | null;
    name?: string | null;
}): void {
    if (!dd) return;
    const cleaned: Record<string, string> = {};
    if (user.id) cleaned.id = user.id;
    if (user.email) cleaned.email = user.email;
    if (user.name) cleaned.name = user.name;
    if (Object.keys(cleaned).length === 0) return;
    dd.setUser(cleaned);
}

export function clearDatadogUser(): void {
    if (!dd) return;
    dd.clearUser();
}

/**
 * Drop-in fetch replacement that logs to Datadog:
 *  - error log on non-OK responses (any duration)
 *  - warn log when a successful response takes > 5000ms
 *  - error log + rethrow on network/throw
 *
 * Pass `ddContext` in init for extra searchable fields, e.g.
 *   loggedFetch('/api/foo', { ddContext: { symbol: 'NVDA' } })
 */
export async function loggedFetch(
    input: RequestInfo | URL,
    init?: RequestInit & { ddContext?: Record<string, unknown> },
): Promise<Response> {
    const { ddContext, ...fetchInit } = init ?? {};
    const url =
        typeof input === "string"
            ? input
            : input instanceof URL
              ? input.toString()
              : input.url;
    const method = fetchInit.method ?? "GET";
    const start =
        typeof performance !== "undefined" ? performance.now() : Date.now();

    try {
        const res = await fetch(input, fetchInit);
        const duration = Math.round(
            (typeof performance !== "undefined"
                ? performance.now()
                : Date.now()) - start,
        );

        if (!res.ok) {
            safeLog("error", "API error", {
                endpoint: url,
                method,
                status: res.status,
                duration,
                ...ddContext,
            });
        } else if (duration > SLOW_API_THRESHOLD_MS) {
            safeLog("warn", "Slow API call", {
                endpoint: url,
                method,
                status: res.status,
                duration,
                ...ddContext,
            });
        }
        return res;
    } catch (err) {
        const duration = Math.round(
            (typeof performance !== "undefined"
                ? performance.now()
                : Date.now()) - start,
        );
        safeLog("error", "API fetch threw", {
            endpoint: url,
            method,
            duration,
            error: err instanceof Error ? err.message : String(err),
            ...ddContext,
        });
        throw err;
    }
}

function safeLog(
    level: "warn" | "error",
    message: string,
    context: Record<string, unknown>,
): void {
    if (!dd) return;
    try {
        if (level === "error") {
            dd.logger.error(message, context);
        } else {
            dd.logger.warn(message, context);
        }
    } catch {
        // Swallow: logging must never break the caller.
    }
}
